import * as am5 from "@amcharts/amcharts5"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5xy from "@amcharts/amcharts5/xy"
import {useEffect} from "react"
import {RootState} from "../../rootReducer"
import {SpinnerGrid} from "../../shared/CommonItems"
import {useAppSelector} from "../../store"

export const PerformanceChart = () => {
    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)
    const performanceCountDetail = useAppSelector(
        (state: RootState) => state.containers.dashboard.performanceCountDetail,
    )
    const isLoadingPerformanceCount = useAppSelector(
        (state: RootState) => state.containers.dashboard.isLoadingPerformanceCount,
    )

    useEffect(() => {
        if (selectedDealer && performanceCountDetail) {
            MultipleColumnLineChart(performanceCountDetail)
        }
    }, [performanceCountDetail, selectedDealer])

    const maybeDisposeRoot = (divId: string) => {
        am5.array.each(am5.registry.rootElements, function (root) {
            if (root && root.dom != null && root.dom.id === divId) {
                root.dispose()
            }
        })
    }
    // Data
    const MultipleColumnLineChart = (performancechartdata: any) => {
        maybeDisposeRoot("performanceChart")

        const root = am5.Root.new("performanceChart")

        am5plugins_exporting.Exporting.new(root, {
            menu: am5plugins_exporting.ExportingMenu.new(root, {}),
            filePrefix: "Overall PerformanceChart",
        })

        root.setThemes([am5themes_Animated.new(root)])

        root.dateFormatter.setAll({
            dateFormat: "yyyy-MM-dd",
            dateFields: ["valueX"],
        })

        const performancechart: any = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: "panX",
                wheelY: "zoomX",
                layout: root.verticalLayout,
            }),
        )

        performancechart.get("colors").set("colors", [
            am5.color(0x99e3dd), //target fee
            am5.color(0x01b8aa), //actual fee
            am5.color(0x0d6abf), //Completed
            am5.color(0x8ad4eb), //confirmed
            am5.color(0xfe9666), //cancelled
            am5.color(0xa43e3b), //noshow
        ])

        const cursor = performancechart.set(
            "cursor",
            am5xy.XYCursor.new(root, {
                behavior: "zoomX",
            }),
        )
        cursor.lineY.set("visible", false)

        const xAxis = performancechart.xAxes.push(
            am5xy.DateAxis.new(root, {
                baseInterval: {timeUnit: "day", count: 1},
                renderer: am5xy.AxisRendererX.new(root, {}),
                tooltip: am5.Tooltip.new(root, {}),
                tooltipDateFormat: "yyyy-MM-dd",
            }),
        )

        const yAxis0 = performancechart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
            }),
        )

        const yRenderer1 = am5xy.AxisRendererY.new(root, {
            opposite: true,
        })
        yRenderer1.grid.template.set("forceHidden", true)

        const yAxis1 = performancechart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: yRenderer1,
                syncWithAxis: yAxis0,
            }),
        )

        const dvsaFeeSeries = performancechart.series.push(
            am5xy.ColumnSeries.new(root, {
                name: "DVSA Fee",
                xAxis,
                yAxis: yAxis0,
                valueYField: "dvsaFee",
                valueXField: "date",
                clustered: false,
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: "horizontal",
                    labelText: "{name}: {valueY}",
                }),
            }),
        )

        dvsaFeeSeries.columns.template.setAll({
            width: am5.percent(60),
            fillOpacity: 0.5,
            strokeOpacity: 0,
        })

        const actualTestFeeSeries = performancechart.series.push(
            am5xy.ColumnSeries.new(root, {
                name: "Actual Test Fee",
                xAxis,
                yAxis: yAxis0,
                valueYField: "actualTestFee",
                valueXField: "date",
                clustered: false,
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: "horizontal",
                    labelText: "[#000] {name} {valueY} [/]",
                }),
            }),
        )

        actualTestFeeSeries.columns.template.set("width", am5.percent(40))

        const {completed,confirmed,cancelled,noShow}= createSerises(root,performancechart,xAxis,yAxis1)

        const scrollbar = performancechart.set(
            "scrollbarX",
            am5xy.XYChartScrollbar.new(root, {
                orientation: "horizontal",
                height: 40,
                start: 0.7,
                end: 0.9,
            }),
        )

        const sbDateAxis = scrollbar.chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                baseInterval: {
                    timeUnit: "day",
                    count: 1,
                },
                renderer: am5xy.AxisRendererX.new(root, {}),
            }),
        )

        const sbValueAxis0 = scrollbar.chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
            }),
        )

        const sbValueAxis1 = scrollbar.chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
            }),
        )

        const actualTestFeeScroll = scrollbar.chart.series.push(
            am5xy.ColumnSeries.new(root, {
                valueYField: "actualTestFee",
                valueXField: "date",
                xAxis: sbDateAxis,
                yAxis: sbValueAxis0,
            }),
        )

        actualTestFeeScroll.columns.template.setAll({fillOpacity: 0.5, strokeOpacity: 0})

        const confirmedScroll = scrollbar.chart.series.push(
            am5xy.LineSeries.new(root, {
                valueYField: "confirmed",
                valueXField: "date",
                xAxis: sbDateAxis,
                yAxis: sbValueAxis1,
            }),
        )

        const legend = performancechart.children.push(
            am5.Legend.new(root, {
                x: am5.p50,
                centerX: am5.p50,
            }),
        )
        legend.data.setAll(performancechart.series.values)

        dvsaFeeSeries.data.setAll(performancechartdata)

        actualTestFeeSeries.data.setAll(performancechartdata)

        completed.data.setAll(performancechartdata)
        confirmed.data.setAll(performancechartdata)
        cancelled.data.setAll(performancechartdata)
        noShow.data.setAll(performancechartdata)

        actualTestFeeScroll.data.setAll(performancechartdata)
        confirmedScroll.data.setAll(performancechartdata)

        performancechart.appear(1000, 100)
    }
    const createSerises=(root:any,performancechart:any,xAxis:any,yAxis1:any)=>{
        const lableText= "[#000]{name} {valueY}[/]"
        const completed = performancechart.series.push(
            am5xy.LineSeries.new(root, {
                name: "Completed",
                xAxis,
                yAxis: yAxis1,
                valueYField: "completed",
                valueXField: "date",
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: "horizontal",
                    labelText: lableText,
                }),
            }),
        )

        completed.strokes.template.setAll({
            strokeWidth: 2,
        })

        const confirmed = performancechart.series.push(
            am5xy.LineSeries.new(root, {
                name: "Confirmed",
                xAxis,
                yAxis: yAxis1,
                valueYField: "confirmed",
                valueXField: "date",
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: "horizontal",
                    labelText: lableText,
                }),
            }),
        )

        confirmed.strokes.template.setAll({
            strokeWidth: 2,
        })
        const cancelled = performancechart.series.push(
            am5xy.LineSeries.new(root, {
                name: "Cancelled",
                xAxis,
                yAxis: yAxis1,
                valueYField: "cancelled",
                valueXField: "date",
            }),
        )

        cancelled.strokes.template.setAll({
            strokeWidth: 2,
        })

        const tooltip1 = cancelled.set(
            "tooltip",
            am5.Tooltip.new(root, {
                pointerOrientation: "horizontal",
            }),
        )
        tooltip1.label.set("text", lableText)
        
        const noShow = performancechart.series.push(
            am5xy.LineSeries.new(root, {
                name: "No Show",
                xAxis,
                yAxis: yAxis1,
                valueYField: "noShow",
                valueXField: "date",
            }),
        )

        noShow.strokes.template.setAll({
            strokeWidth: 2,
        })

        const tooltip2 = noShow.set(
            "tooltip",
            am5.Tooltip.new(root, {
                pointerOrientation: "horizontal",
            }),
        )
        tooltip2.label.set("text", lableText)

        return {completed,confirmed,cancelled,noShow}
    }
    return (
        <>
            {isLoadingPerformanceCount && <SpinnerGrid />}
            <div className="h-100" id="performanceChart"></div>
        </>
    )
}
